import { useEffect, useMemo, useState } from "react";
import { useConvex, useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Doc } from "../../convex/_generated/dataModel";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { Toggle } from "@/components/Toggle";
import { cn } from "@/lib/cn";
import { getWhatsAppWebhookUrl } from "@/lib/whatsapp-url";
import { useDemoMode } from "@/lib/demo-mode";
import { requestAppTourReplay } from "../components/tour/tour-storage";
import {
  RERUN_ONBOARDING_UPDATE,
  downloadJsonString,
  loggingStyleUpdate,
  runDataExport,
} from "./settings-export";
import {
  EATING_OPTIONS,
  EXERCISE_OPTIONS,
  EXERCISE_TYPE_OPTIONS,
  IMPACT_OPTIONS,
  LOGGING_OPTIONS,
  MOTIVATION_OPTIONS,
  PACE_OPTIONS,
  SAFETY_OPTIONS,
  STEPS_BUCKET_MIDPOINTS,
  STEPS_OPTIONS,
  cmToFtIn,
  displayToKg,
  ftInToCm,
  kgToDisplay,
  stepsBucketForValue,
  type UnitHeight,
  type UnitWeight,
} from "../onboarding/types";
import {
  AUDIO_LANGUAGES,
  AUDIO_MODELS,
  DEFAULT_AUDIO_MODEL,
  type AudioModelId,
  isSupportedAudioLanguage,
  isSupportedAudioModel,
} from "./audio-preferences";
import {
  AudioLines,
  BookOpen,
  Check,
  Download,
  Gauge,
  Globe,
  Info,
  LifeBuoy,
  ListChecks,
  RotateCcw,
  Search,
  ShieldCheck,
  Sliders,
  Smartphone,
  Sparkles,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import {
  DEFAULT_CHAT_MODEL,
  DEFAULT_VISION_MODEL,
  buildModelOptions,
  isChatModelId,
  isVisionModelId,
} from "@/lib/model-list";

type User = Doc<"users">;
type UserProfile = User & { dietType?: DietType };

const GENDERS = ["male", "female", "other"] as const;
const ACTIVITY_LEVELS = [
  "sedentary",
  "light",
  "moderate",
  "active",
  "very_active",
] as const;
const GOAL_TYPES = ["lose", "maintain", "gain", "muscle"] as const;
const DIET_TYPES = ["keto", "low-carb", "balanced", "high-protein", "custom"] as const;
type DietType = (typeof DIET_TYPES)[number];

function isDietType(value: string): value is DietType {
  return DIET_TYPES.some((diet) => diet === value);
}

function asUnitWeight(value: string): UnitWeight {
  return value === "lbs" ? "lbs" : "kg";
}

function formatWeightDisplay(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

const SELECT_CLASSES = cn(
  "min-h-[44px] w-full min-w-0 truncate rounded-xl border border-border bg-surface py-3 px-4 text-base",
  "text-text focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
);

const SECTION_CLASSES = "rounded-2xl border border-border bg-surface p-5";
const SECTION_SUMMARY_CLASSES = cn(
  "flex min-h-[44px] cursor-pointer list-none items-center gap-2",
  "text-base font-semibold text-text active:opacity-80",
);

const AUTO_TIMEZONE = "auto";
const COMMON_TIMEZONES: readonly string[] = [
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Istanbul",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Riyadh",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Africa/Cairo",
  "Africa/Lagos",
  "Africa/Johannesburg",
];

const CHAT_AGENT_INSTRUCTIONS_MAX = 2000;

function UnitToggle({
  value,
  options,
  onChange,
}: {
  value: string;
  options: [string, string];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex rounded-xl border border-border bg-muted p-1">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={cn(
            "flex-1 min-h-[44px] rounded-lg py-2 text-sm font-medium transition-colors",
            "active:scale-[0.98] select-none",
            value === opt
              ? "bg-surface text-text shadow-sm"
              : "text-text-muted",
          )}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export interface SettingsProps {
  onSwitchScreen?: (screen: string) => void;
}

export default function SettingsScreen({ onSwitchScreen }: SettingsProps) {
  const { enableDemo } = useDemoMode();
  const profile = useQuery(api.profile.getProfile, {}) as UserProfile | null | undefined;
  const preferences = useQuery(api.profile.getPreferences, {});
  const selectedModel = useQuery(api.settings.getSelectedModel, {});
  const selectedVisionModel = useQuery(api.settings.getSelectedVisionModel, {});
  const audioPreferences = useQuery(api.settings.getAudioPreferences, {});
  const demoConfig = useQuery(api.demo.getConfig, {});
  const setDemoConfig = useMutation(api.demo.setConfig);

  const listModels = useAction(api.integrations.listModels);
  const getWhatsAppState = useAction(api.integrations.getWhatsAppConnectionState);
  const getWhatsAppWebhookReadiness = useAction(
    api.integrations.getWhatsAppWebhookReadiness,
  );
  const beginWhatsAppConsent = useAction(api.integrations.beginWhatsAppConsent);
  const pollWhatsAppConsent = useAction(api.integrations.pollWhatsAppConsent);
  const generateLinkingCode = useMutation(api.whatsapp.generateLinkingCode);
  const resetEverything = useMutation(api.reset.resetEverything);
  const convex = useConvex();
  const engineState = useQuery(api.engine.getEngineState, {});

  const updateProfile = useMutation(api.profile.updateProfile).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.profile.getProfile, {});
      if (current === undefined || current === null) return;
      const requestedDietType = args.dietType;
      const nextDietType =
        requestedDietType !== undefined && isDietType(requestedDietType)
          ? requestedDietType
          : current.dietType;
      const updated: User = { ...current, ...args, dietType: nextDietType };
      localStore.setQuery(api.profile.getProfile, {}, updated);
    },
  );

  const setPreferences = useMutation(api.profile.setPreferences).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.profile.getPreferences, {});
      if (current === undefined) return;
      localStore.setQuery(api.profile.getPreferences, {}, { ...current, ...args });
    },
  );

  const setModel = useMutation(api.settings.setSelectedModel).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.settings.getSelectedModel, {});
      if (current === undefined) return;
      if (isChatModelId(args.model)) {
        localStore.setQuery(api.settings.getSelectedModel, {}, args.model);
      }
    },
  );

  const setVisionModel = useMutation(
    api.settings.setSelectedVisionModel,
  ).withOptimisticUpdate((localStore, args) => {
    const current = localStore.getQuery(api.settings.getSelectedVisionModel, {});
    if (current === undefined) return;
    localStore.setQuery(api.settings.getSelectedVisionModel, {}, args.model);
  });

  const setAudioPreferences = useMutation(api.settings.setAudioPreferences).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.settings.getAudioPreferences, {});
      if (current === undefined) return;
      localStore.setQuery(api.settings.getAudioPreferences, {}, { ...current, ...args });
    },
  );

  const [dailyGoal, setDailyGoal] = useState("");
  const [dailyProtein, setDailyProtein] = useState("");
  const [dailyCarbs, setDailyCarbs] = useState("");
  const [dailyFat, setDailyFat] = useState("");
  const [dailyFiber, setDailyFiber] = useState("");
  const [weight, setWeight] = useState("");
  const [targetWeight, setTargetWeight] = useState("");
  const [height, setHeight] = useState("");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [heightUnit, setHeightUnit] = useState<UnitHeight>("cm");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState<string>("");
  const [activityLevel, setActivityLevel] = useState<string>("");
  const [goalType, setGoalType] = useState<string>("");
  const [dietType, setDietType] = useState<DietType>("balanced");
  const [motivation, setMotivation] = useState<string>("");
  const [impact, setImpact] = useState<string>("");
  const [exerciseFrequency, setExerciseFrequency] = useState<string>("");
  const [exerciseType, setExerciseType] = useState<string>("");
  const [stepsBucket, setStepsBucket] = useState<string>("");
  const [bodyFat, setBodyFat] = useState("");
  const [waist, setWaist] = useState("");
  const [safetyFlags, setSafetyFlags] = useState<string[]>([]);
  const [weightLossPace, setWeightLossPace] = useState<string>("");
  const [loggingStyle, setLoggingStyle] = useState<string>("");
  const [eatingStyle, setEatingStyle] = useState<string>("");
  const [unitWeight, setUnitWeight] = useState<string>("kg");
  const [unitDistance, setUnitDistance] = useState<string>("km");
  const [savingProfile, setSavingProfile] = useState(false);
  const [timezoneStatus, setTimezoneStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [agentInstructions, setAgentInstructions] = useState("");
  const [agentInstructionsStatus, setAgentInstructionsStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunBusy, setRerunBusy] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportDone, setExportDone] = useState(false);

  const [models, setModels] = useState<{ id: string; owned_by?: string }[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState<string | null>(null);

  const [whatsApp, setWhatsApp] = useState<{
    connected: boolean;
    providerId?: string;
    fromPhoneNumberId?: string;
  } | null>(null);
  const [whatsAppLoading, setWhatsAppLoading] = useState(true);
  const [whatsAppError, setWhatsAppError] = useState<string | null>(null);
  const [whatsAppWebhook, setWhatsAppWebhook] = useState<{ configured: boolean } | null>(null);
  const [whatsAppWebhookLoading, setWhatsAppWebhookLoading] = useState(true);
  const [whatsAppWebhookError, setWhatsAppWebhookError] = useState<string | null>(null);
  const [waPhoneId, setWaPhoneId] = useState("");
  const [waBusy, setWaBusy] = useState(false);
  const [linkingCode, setLinkingCode] = useState<string | null>(null);
  const [linkingBusy, setLinkingBusy] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [demoInstallUrl, setDemoInstallUrl] = useState("");
  const [demoPassword, setDemoPassword] = useState("");
  const [demoSaving, setDemoSaving] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [demoSaved, setDemoSaved] = useState(false);

  const [modelValue, setModelValue] = useState<string>(DEFAULT_CHAT_MODEL);
  const [visionModel, setVisionModelValue] = useState<string>(
    DEFAULT_VISION_MODEL,
  );
  const [audioModel, setAudioModel] = useState<AudioModelId>(DEFAULT_AUDIO_MODEL);
  const [audioLanguage, setAudioLanguage] = useState("auto");
  const whatsAppWebhookUrl = useMemo(() => getWhatsAppWebhookUrl(), []);

  const detectedTimezone = useMemo(() => {
    try {
      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return typeof zone === "string" && zone.length > 0 ? zone : "UTC";
    } catch {
      console.warn("[Frontend][Settings] Could not detect device time zone; using UTC");
      return "UTC";
    }
  }, []);
  const profileTimezone = profile?.timezone ?? AUTO_TIMEZONE;
  const timezoneOptions = useMemo(() => {
    const zones = [...COMMON_TIMEZONES];
    for (const extra of [detectedTimezone, profileTimezone]) {
      if (extra !== AUTO_TIMEZONE && !zones.includes(extra)) zones.push(extra);
    }
    return zones;
  }, [detectedTimezone, profileTimezone]);
  const effectiveTimezone =
    profileTimezone === AUTO_TIMEZONE ? detectedTimezone : profileTimezone;

  useEffect(() => {
    console.log("[Frontend][Settings] Screen mounted", {
      hasProfile: profile !== undefined && profile !== null,
    });
  }, [profile]);

  useEffect(() => {
    if (profile === undefined) return;
    if (profile === null) return;
    setDailyGoal(profile.dailyGoalKcal ? String(profile.dailyGoalKcal) : "");
    setDailyProtein(
      profile.dailyProteinG !== undefined ? String(profile.dailyProteinG) : "",
    );
    setDailyCarbs(
      profile.dailyCarbsG !== undefined ? String(profile.dailyCarbsG) : "",
    );
    setDailyFat(profile.dailyFatG !== undefined ? String(profile.dailyFatG) : "");
    setDailyFiber(
      profile.dailyFiberG !== undefined ? String(profile.dailyFiberG) : "",
    );
    const nextUnitWeight = profile.unitWeight ?? "kg";
    const weightUnit = asUnitWeight(nextUnitWeight);
    setWeight(
      profile.weightKg !== undefined
        ? formatWeightDisplay(kgToDisplay(profile.weightKg, weightUnit))
        : "",
    );
    setTargetWeight(
      profile.targetWeightKg !== undefined
        ? formatWeightDisplay(kgToDisplay(profile.targetWeightKg, weightUnit))
        : "",
    );
    if (profile.heightCm !== undefined) {
      setHeight(String(profile.heightCm));
      const { ft, inch } = cmToFtIn(profile.heightCm);
      setHeightFt(String(ft));
      setHeightIn(String(inch));
    } else {
      setHeight("");
      setHeightFt("");
      setHeightIn("");
    }
    setAge(profile.age ? String(profile.age) : "");
    setGender(profile.gender ?? "");
    setActivityLevel(profile.activityLevel ?? "");
    setGoalType(profile.goalType ?? "");
    setDietType(profile.dietType ?? "balanced");
    setMotivation(profile.motivation ?? "");
    setImpact(profile.impact ?? "");
    setExerciseFrequency(profile.exerciseFrequency ?? "");
    setExerciseType(profile.exerciseType ?? "");
    setStepsBucket(
      profile.avgDailySteps !== undefined ? stepsBucketForValue(profile.avgDailySteps) : "",
    );
    setBodyFat(profile.bodyFatPercent !== undefined ? String(profile.bodyFatPercent) : "");
    setWaist(profile.waistCm !== undefined ? String(profile.waistCm) : "");
    setSafetyFlags(profile.safetyFlags !== undefined ? [...profile.safetyFlags] : []);
    setWeightLossPace(profile.weightLossPace ?? "");
    setLoggingStyle(profile.loggingStyle ?? "");
    setEatingStyle(profile.eatingStyle ?? "");
    setUnitWeight(nextUnitWeight);
    setUnitDistance(profile.unitDistance ?? "km");
    setAgentInstructions(profile.chatAgentInstructions ?? "");
  }, [profile]);

  useEffect(() => {
    if (selectedModel === undefined) return;
    setModelValue(
      selectedModel !== null &&
        selectedModel !== "" &&
        isChatModelId(selectedModel)
        ? selectedModel
        : DEFAULT_CHAT_MODEL,
    );
  }, [selectedModel]);

  useEffect(() => {
    if (selectedVisionModel === undefined) return;
    setVisionModelValue(
      selectedVisionModel !== null && isVisionModelId(selectedVisionModel)
        ? selectedVisionModel
        : DEFAULT_VISION_MODEL,
    );
  }, [selectedVisionModel]);

  useEffect(() => {
    if (audioPreferences === undefined) return;
    if (isSupportedAudioModel(audioPreferences.audioModel)) {
      const matchingModel = AUDIO_MODELS.find((model) => model.id === audioPreferences.audioModel);
      if (matchingModel) setAudioModel(matchingModel.id);
    }
    setAudioLanguage(audioPreferences.audioLanguage);
  }, [audioPreferences]);

  useEffect(() => {
    if (demoConfig === undefined) return;
    setDemoEnabled(demoConfig.enabled);
    setDemoInstallUrl(demoConfig.installUrl);
  }, [demoConfig]);

  useEffect(() => {
    let cancelled = false;
    setModelsLoading(true);
    listModels({})
      .then((result) => {
        if (!cancelled) {
          setModels(result.data);
          setModelsError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setModelsError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listModels]);

  const chatModelOptions = useMemo(
    () =>
      buildModelOptions(
        models.map((model) => model.id),
        isChatModelId,
        DEFAULT_CHAT_MODEL,
        modelValue,
      ),
    [models, modelValue],
  );
  const visionModelOptions = useMemo(
    () =>
      buildModelOptions(
        models.map((model) => model.id),
        isVisionModelId,
        DEFAULT_VISION_MODEL,
        visionModel,
      ),
    [models, visionModel],
  );

  const loadWhatsApp = () => {
    setWhatsAppLoading(true);
    setWhatsAppError(null);
    getWhatsAppState({})
      .then((result) => {
        setWhatsApp(result);
      })
      .catch((err: unknown) => {
        setWhatsAppError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setWhatsAppLoading(false));
  };

  const loadWhatsAppWebhookReadiness = () => {
    setWhatsAppWebhookLoading(true);
    setWhatsAppWebhookError(null);
    getWhatsAppWebhookReadiness({})
      .then((result) => setWhatsAppWebhook(result))
      .catch((err: unknown) => {
        console.warn("[Frontend][Settings] WhatsApp webhook readiness unavailable", {
          error: err instanceof Error ? err.message : String(err),
        });
        setWhatsAppWebhookError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setWhatsAppWebhookLoading(false));
  };

  useEffect(() => {
    loadWhatsApp();
  }, [getWhatsAppState]);

  useEffect(() => {
    loadWhatsAppWebhookReadiness();
  }, [getWhatsAppWebhookReadiness]);

  const handleUnitWeightChange = (next: string) => {
    if (next === unitWeight) return;
    const prevUnit = asUnitWeight(unitWeight);
    const nextUnit = asUnitWeight(next);
    const convertDisplay = (raw: string): string => {
      if (raw.trim() === "") return raw;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return raw;
      return formatWeightDisplay(kgToDisplay(displayToKg(parsed, prevUnit), nextUnit));
    };
    setWeight((current) => convertDisplay(current));
    setTargetWeight((current) => convertDisplay(current));
    console.log("[Frontend][Settings] Weight unit toggled", {
      from: prevUnit,
      to: nextUnit,
    });
    setUnitWeight(next);
  };

  const handleHeightUnitChange = (nextLabel: string) => {
    const next: UnitHeight = nextLabel === "ft/in" ? "ftin" : "cm";
    if (next === heightUnit) return;
    if (next === "ftin") {
      const parsedCm = height.trim() === "" ? undefined : Number(height);
      if (parsedCm !== undefined && Number.isFinite(parsedCm)) {
        const { ft, inch } = cmToFtIn(parsedCm);
        setHeightFt(String(ft));
        setHeightIn(String(inch));
      }
    } else if (heightFt.trim() !== "" || heightIn.trim() !== "") {
      const ft = heightFt.trim() === "" ? 0 : Number(heightFt);
      const inch = heightIn.trim() === "" ? 0 : Number(heightIn);
      if (Number.isFinite(ft) && Number.isFinite(inch)) {
        setHeight(formatWeightDisplay(ftInToCm(ft, inch)));
      }
    }
    console.log("[Frontend][Settings] Height unit toggled", {
      from: heightUnit,
      to: next,
    });
    setHeightUnit(next);
  };

  const handleSaveProfile = async () => {
    const dailyGoalKcal = dailyGoal ? Number(dailyGoal) : undefined;
    const dailyProteinG = dailyProtein ? Number(dailyProtein) : undefined;
    const dailyCarbsG = dailyCarbs ? Number(dailyCarbs) : undefined;
    const dailyFatG = dailyFat ? Number(dailyFat) : undefined;
    const dailyFiberG = dailyFiber ? Number(dailyFiber) : undefined;
    const weightUnit = asUnitWeight(unitWeight);
    const weightKg = weight ? displayToKg(Number(weight), weightUnit) : undefined;
    const targetWeightKg = targetWeight
      ? displayToKg(Number(targetWeight), weightUnit)
      : undefined;
    const heightCm =
      heightUnit === "cm"
        ? height
          ? Number(height)
          : undefined
        : heightFt.trim() !== "" || heightIn.trim() !== ""
          ? ftInToCm(
              heightFt.trim() === "" ? 0 : Number(heightFt),
              heightIn.trim() === "" ? 0 : Number(heightIn),
            )
          : undefined;
    const ageNum = age ? Number(age) : undefined;
    const avgDailySteps = stepsBucket !== "" ? STEPS_BUCKET_MIDPOINTS[stepsBucket] : undefined;
    const bodyFatPercent = bodyFat.trim() !== "" ? Number(bodyFat) : undefined;
    const waistCm = waist.trim() !== "" ? Number(waist) : undefined;
    if (bodyFatPercent !== undefined && (!Number.isFinite(bodyFatPercent) || bodyFatPercent < 3 || bodyFatPercent > 60)) {
      console.warn("[Frontend][Settings] Invalid body-fat percentage");
      return;
    }
    if (waistCm !== undefined && (!Number.isFinite(waistCm) || waistCm < 30 || waistCm > 300)) {
      console.warn("[Frontend][Settings] Invalid waist measurement");
      return;
    }

    if (
      dailyGoalKcal !== undefined &&
      (!Number.isFinite(dailyGoalKcal) || dailyGoalKcal <= 0)
    ) {
      return;
    }

    const nutritionTargets = [
      ["dailyProteinG", dailyProteinG],
      ["dailyCarbsG", dailyCarbsG],
      ["dailyFatG", dailyFatG],
      ["dailyFiberG", dailyFiberG],
    ] as const;
    if (nutritionTargets.some(([, value]) => value !== undefined && (!Number.isFinite(value) || value < 0))) {
      console.warn("[Frontend][Settings] Invalid nutrition target", {
        targetCount: nutritionTargets.filter(([, value]) => value !== undefined).length,
      });
      return;
    }

    setSavingProfile(true);
    console.log("[Frontend][Settings] Saving profile", {
      hasDailyGoal: dailyGoalKcal !== undefined,
      hasNutritionTargets: nutritionTargets.some(([, value]) => value !== undefined),
      hasBodyMetrics:
        weightKg !== undefined || heightCm !== undefined || ageNum !== undefined,
      hasTargetWeight: targetWeightKg !== undefined,
      hasOnboardingAnswers:
        motivation !== "" ||
        impact !== "" ||
        exerciseFrequency !== "" ||
        weightLossPace !== "" ||
        loggingStyle !== "" ||
        eatingStyle !== "",
      hasDietType: dietType !== undefined,
    });
    try {
      const profileUpdate = {
        ...(dailyGoalKcal !== undefined ? { dailyGoalKcal } : {}),
        ...(dailyProteinG !== undefined ? { dailyProteinG } : {}),
        ...(dailyCarbsG !== undefined ? { dailyCarbsG } : {}),
        ...(dailyFatG !== undefined ? { dailyFatG } : {}),
        ...(dailyFiberG !== undefined ? { dailyFiberG } : {}),
        ...(weightKg !== undefined ? { weightKg } : {}),
        ...(targetWeightKg !== undefined ? { targetWeightKg } : {}),
        ...(heightCm !== undefined ? { heightCm } : {}),
        ...(ageNum !== undefined ? { age: ageNum } : {}),
        ...(gender ? { gender } : {}),
        ...(activityLevel ? { activityLevel } : {}),
        ...(goalType ? { goalType } : {}),
        ...(motivation ? { motivation } : {}),
        ...(impact ? { impact } : {}),
        ...(exerciseFrequency ? { exerciseFrequency } : {}),
        ...(exerciseType ? { exerciseType } : {}),
        ...(avgDailySteps !== undefined ? { avgDailySteps } : {}),
        ...(bodyFatPercent !== undefined ? { bodyFatPercent } : {}),
        ...(waistCm !== undefined ? { waistCm } : {}),
        safetyFlags,
        ...(weightLossPace ? { weightLossPace } : {}),
        ...(loggingStyle ? { loggingStyle } : {}),
        ...(eatingStyle ? { eatingStyle } : {}),
        dietType,
        unitWeight,
        unitDistance,
      };
      await updateProfile(profileUpdate);
      console.log("[Frontend][Settings] Profile saved");
    } catch (err) {
      console.error("[Frontend][Settings] Profile save failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleToggleSafetyFlag = (flag: string) => {
    setSafetyFlags((prev) =>
      prev.includes(flag) ? prev.filter((entry) => entry !== flag) : [...prev, flag],
    );
  };

  const handleResetEverything = async () => {
    if (resetBusy) return;
    setResetBusy(true);
    setResetError(null);
    console.log("[Frontend][Settings] Reset everything confirmed");
    try {
      await resetEverything({});
      console.log("[Frontend][Settings] Reset everything succeeded");
      // Onboarding renders automatically: getProfile is reactive and
      // onboardingComplete is now false (App.tsx AuthenticatedApp gate).
    } catch (err) {
      console.error("[Frontend][Settings] Reset everything failed", {
        error: err instanceof Error ? err.message : String(err),
      });
      setResetError(
        err instanceof Error
          ? err.message
          : "Reset failed. Check your connection and try again.",
      );
    } finally {
      setResetBusy(false);
    }
  };

  const handleModelChange = async (value: string) => {
    if (!value) return;
    if (!isChatModelId(value)) {
      console.warn("[Frontend][Settings] Model rejected by picker predicate", {
        model: value,
      });
      return;
    }
    setModelValue(value);
    console.log("[Frontend][Settings] Model selected", { model: value });
    try {
      await setModel({ model: value });
    } catch (err) {
      console.error("[Frontend][Settings] Model selection failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleVisionModelChange = async (value: string) => {
    if (!isVisionModelId(value)) return;
    setVisionModelValue(value);
    console.log("[Frontend][Settings] Vision model selected", { model: value });
    try {
      await setVisionModel({ model: value });
    } catch (err: unknown) {
      console.error("[Frontend][Settings] Vision model selection failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleAudioModelChange = async (value: string) => {
    if (!isSupportedAudioModel(value)) return;
    setAudioModel(value);
    console.log("[Frontend][Settings] Audio model selected", { model: value });
    try {
      await setAudioPreferences({ audioModel: value, audioLanguage });
    } catch (err: unknown) {
      console.error("[Frontend][Settings] Audio model save failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleAudioLanguageChange = async (value: string) => {
    if (!isSupportedAudioLanguage(value)) return;
    setAudioLanguage(value);
    console.log("[Frontend][Settings] Audio language selected", { language: value });
    try {
      await setAudioPreferences({ audioModel, audioLanguage: value });
    } catch (err: unknown) {
      console.error("[Frontend][Settings] Audio language save failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleConnectWhatsApp = async () => {
    if (!waPhoneId.trim() || whatsAppWebhookUrl === null) {
      return;
    }
    setWaBusy(true);
    const consentWindow = window.open("", "_blank");
    console.log("[Frontend][Settings] Connecting WhatsApp", {
      phoneNumberId: waPhoneId,
    });
    try {
      const started = await beginWhatsAppConsent({
        action: "grant",
        phoneNumberId: waPhoneId.trim(),
      });
      if (consentWindow !== null) {
        consentWindow.location.href = started.consentUrl;
      } else {
        window.open(started.consentUrl, "_blank");
      }
      const result = await pollWhatsAppConsent({ state: started.state });
      if (result.status !== "completed") {
        throw new Error(result.errorCode ?? "WhatsApp connection was not completed");
      }
      setWaPhoneId("");
      loadWhatsApp();
    } catch (err) {
      consentWindow?.close();
      setWhatsAppError(err instanceof Error ? err.message : String(err));
    } finally {
      setWaBusy(false);
    }
  };

  const handleDisconnectWhatsApp = async () => {
    setWaBusy(true);
    console.log("[Frontend][Settings] Disconnecting WhatsApp");
    const consentWindow = window.open("", "_blank");
    try {
      const started = await beginWhatsAppConsent({ action: "revoke" });
      if (consentWindow !== null) {
        consentWindow.location.href = started.consentUrl;
      } else {
        window.open(started.consentUrl, "_blank");
      }
      const result = await pollWhatsAppConsent({ state: started.state });
      if (result.status !== "completed") {
        throw new Error(result.errorCode ?? "WhatsApp disconnection was not completed");
      }
      loadWhatsApp();
    } catch (err) {
      consentWindow?.close();
      setWhatsAppError(err instanceof Error ? err.message : String(err));
    } finally {
      setWaBusy(false);
    }
  };

  const handleLinkNewNumber = async () => {
    setLinkingBusy(true);
    console.log("[Frontend][Settings] Generating WhatsApp linking code");
    try {
      const code = await generateLinkingCode({});
      setLinkingCode(code);
    } catch (err) {
      console.error("[Frontend][Settings] Linking code failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLinkingBusy(false);
    }
  };

  const handleTogglePreference = async (
    key: "alwaysAsk" | "autoMemory" | "webSearch" | "autoSendImage" | "autoSendVoice",
    value: boolean,
  ) => {
    if (preferences === undefined) return;
    const next = {
      alwaysAsk: preferences?.alwaysAsk ?? true,
      autoMemory: preferences?.autoMemory ?? true,
      webSearch: preferences?.webSearch ?? true,
      autoSendImage: preferences?.autoSendImage ?? true,
      autoSendVoice: preferences?.autoSendVoice ?? true,
      [key]: value,
    };
    console.log("[Frontend][Settings] Toggling preference", { key, value });
    try {
      await setPreferences(next);
    } catch (err) {
      console.error("[Frontend][Settings] Preference save failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleSaveDemoConfig = async () => {
    const installUrl = demoInstallUrl.trim();
    if (installUrl.length > 0) {
      try {
        const parsed = new URL(installUrl);
        if (parsed.protocol !== "https:") throw new TypeError("HTTPS required");
      } catch (error: unknown) {
        console.warn("[Frontend][Settings] Invalid demo install URL", { error: error instanceof Error ? error.message : String(error) });
        setDemoError("Install URL must be a valid HTTPS URL.");
        return;
      }
    }
    if (demoEnabled && demoPassword.trim().length === 0 && demoConfig?.configured !== true) {
      setDemoError("Enter a password before enabling demo mode.");
      return;
    }
    setDemoSaving(true);
    setDemoError(null);
    setDemoSaved(false);
    try {
      await setDemoConfig({
        enabled: demoEnabled,
        installUrl,
        ...(demoPassword.trim().length > 0 ? { password: demoPassword } : {}),
      });
      setDemoPassword("");
      setDemoSaved(true);
      console.log("[Frontend][Settings] Demo configuration saved", { enabled: demoEnabled, hasInstallUrl: installUrl.length > 0 });
      if (demoEnabled) enableDemo(installUrl);
    } catch (error: unknown) {
      console.error("[Frontend][Settings] Demo configuration save failed", { error: error instanceof Error ? error.message : String(error) });
      setDemoError(error instanceof Error ? error.message : "Demo configuration could not be saved.");
    } finally {
      setDemoSaving(false);
    }
  };

  const handleLoggingStyleChange = async (value: string) => {
    setLoggingStyle(value);
    const payload = loggingStyleUpdate(value);
    if (payload === null) return;
    console.log("[Frontend][Settings] Logging style changed", payload);
    try {
      await updateProfile(payload);
    } catch (err) {
      console.error("[Frontend][Settings] Logging style save failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleTimezoneChange = async (value: string) => {
    if (value === profileTimezone) return;
    setTimezoneStatus("saving");
    console.log("[Frontend][Settings] Timezone change requested", { timezone: value });
    try {
      await updateProfile({ timezone: value });
      setTimezoneStatus("saved");
      console.log("[Frontend][Settings] Timezone saved", { timezone: value });
    } catch (err) {
      setTimezoneStatus("error");
      console.error("[Frontend][Settings] Timezone save failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleSaveAgentInstructions = async () => {
    const trimmed = agentInstructions.trim();
    if (trimmed.length > CHAT_AGENT_INSTRUCTIONS_MAX) {
      setAgentInstructionsStatus("error");
      console.warn("[Frontend][Settings] Agent instructions exceed limit", {
        length: trimmed.length,
      });
      return;
    }
    setAgentInstructionsStatus("saving");
    console.log("[Frontend][Settings] Saving chat agent instructions", {
      length: trimmed.length,
    });
    try {
      await updateProfile({ chatAgentInstructions: trimmed });
      setAgentInstructionsStatus("saved");
      console.log("[Frontend][Settings] Chat agent instructions saved");
    } catch (err) {
      setAgentInstructionsStatus("error");
      console.error("[Frontend][Settings] Chat agent instructions save failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleRerunOnboarding = async () => {
    if (rerunBusy) return;
    setRerunBusy(true);
    setRerunError(null);
    console.log("[Frontend][Settings] Rerun onboarding confirmed");
    try {
      await updateProfile(RERUN_ONBOARDING_UPDATE);
    } catch (err) {
      console.error("[Frontend][Settings] Rerun onboarding failed", {
        error: err instanceof Error ? err.message : String(err),
      });
      setRerunError(
        err instanceof Error
          ? err.message
          : "Could not restart onboarding. Check your connection and try again.",
      );
      setRerunBusy(false);
    }
  };

  const handleExportData = async () => {
    if (exportBusy) return;
    setExportBusy(true);
    setExportError(null);
    setExportDone(false);
    console.log("[Frontend][Settings] Export data requested");
    const result = await runDataExport({
      fetchEntries: (window) =>
        convex.query(api.entries.listByDateRange, {
          start: window.start,
          end: window.end,
        }),
      fetchWeights: () => convex.query(api.weight.list, {}),
      fetchMemories: () => convex.query(api.foodMemory.listAll, {}),
      profile: profile ?? null,
      now: Date.now(),
      download: (filename, json) => {
        downloadJsonString(filename, json, {
          createObjectURL: (blob) => URL.createObjectURL(blob),
          revokeObjectURL: (url) => URL.revokeObjectURL(url),
          clickAnchor: (href, download) => {
            const anchor = document.createElement("a");
            anchor.href = href;
            anchor.download = download;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
          },
        });
      },
    });
    if (result.ok) {
      console.log("[Frontend][Settings] Export data downloaded", {
        filename: result.filename,
      });
      setExportDone(true);
    } else {
      console.warn("[Frontend][Settings] Export data failed", {
        reason: result.message,
      });
      setExportError(
        "Export failed — nothing was downloaded. Check your connection and try again.",
      );
    }
    setExportBusy(false);
  };

  if (profile === undefined) {
    return (
      <div data-testid="settings-container" className="flex flex-col gap-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
      </div>
    );
  }

  return (
    <div data-testid="settings-container" className="flex flex-col gap-6 pb-8">
      {/* Section 1: Profile and programme */}
      <details open data-testid="settings-section-profile" className={SECTION_CLASSES}>
        <summary className={SECTION_SUMMARY_CLASSES}>
          <Sliders size={20} className="text-text-muted" />
          <span>Profile and programme</span>
        </summary>
        <div className="mt-4 flex flex-col gap-4">
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-2">
          <Sliders size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">Profile</h3>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Daily goal (kcal)"
            type="number"
            inputMode="numeric"
            value={dailyGoal}
            onChange={(e) => setDailyGoal(e.target.value)}
            placeholder="2000"
          />
          <Input
            label={`Weight (${asUnitWeight(unitWeight)})`}
            type="number"
            inputMode="decimal"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder={asUnitWeight(unitWeight) === "lbs" ? "154" : "70"}
          />
          <Input
            data-testid="target-weight-input"
            label={`Target weight (${asUnitWeight(unitWeight)})`}
            type="number"
            inputMode="decimal"
            value={targetWeight}
            onChange={(e) => setTargetWeight(e.target.value)}
            placeholder={asUnitWeight(unitWeight) === "lbs" ? "143" : "65"}
          />
          <Input
            label="Age"
            type="number"
            inputMode="numeric"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            placeholder="30"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text">Height</label>
          {heightUnit === "cm" ? (
            <Input
              aria-label="Height in cm"
              type="number"
              inputMode="numeric"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="175"
            />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Input
                aria-label="Height in feet"
                type="number"
                inputMode="numeric"
                value={heightFt}
                onChange={(e) => setHeightFt(e.target.value)}
                placeholder="5"
              />
              <Input
                aria-label="Height in inches"
                type="number"
                inputMode="numeric"
                value={heightIn}
                onChange={(e) => setHeightIn(e.target.value)}
                placeholder="9"
              />
            </div>
          )}
          <div role="group" aria-label="Height unit">
            <UnitToggle
              value={heightUnit === "cm" ? "cm" : "ft/in"}
              options={["cm", "ft/in"]}
              onChange={handleHeightUnitChange}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            data-testid="body-fat-input"
            label="Body-fat % (optional)"
            type="number"
            inputMode="decimal"
            min="3"
            max="60"
            value={bodyFat}
            onChange={(e) => setBodyFat(e.target.value)}
            placeholder="20"
          />
          <Input
            data-testid="waist-input"
            label="Waist (cm, optional)"
            type="number"
            inputMode="decimal"
            min="30"
            max="300"
            value={waist}
            onChange={(e) => setWaist(e.target.value)}
            placeholder="80"
          />
        </div>
        <p className="-mt-2 text-xs leading-relaxed text-text-muted">
          Optional measurements improve estimate accuracy. Leave blank if unsure — consumer
          estimates can be inaccurate.
        </p>

        <div className="rounded-2xl border border-border bg-muted/60 p-4">
          <div>
            <h4 className="text-sm font-semibold text-text">Daily nutrition targets</h4>
            <p className="mt-1 text-xs leading-relaxed text-text-muted">
              Optional targets power the macro and fiber rings in Progress.
            </p>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Input
              data-testid="daily-protein-input"
              label="Protein (g)"
              type="number"
              inputMode="decimal"
              min="0"
              value={dailyProtein}
              onChange={(e) => setDailyProtein(e.target.value)}
              placeholder="120"
            />
            <Input
              data-testid="daily-carbs-input"
              label="Carbs (g)"
              type="number"
              inputMode="decimal"
              min="0"
              value={dailyCarbs}
              onChange={(e) => setDailyCarbs(e.target.value)}
              placeholder="180"
            />
            <Input
              data-testid="daily-fat-input"
              label="Fat (g)"
              type="number"
              inputMode="decimal"
              min="0"
              value={dailyFat}
              onChange={(e) => setDailyFat(e.target.value)}
              placeholder="60"
            />
            <Input
              data-testid="daily-fiber-input"
              label="Fiber (g)"
              type="number"
              inputMode="decimal"
              min="0"
              value={dailyFiber}
              onChange={(e) => setDailyFiber(e.target.value)}
              placeholder="28"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text">Gender</label>
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className={cn(
              "w-full rounded-xl border border-border bg-surface py-3 px-4 text-base",
              "text-text focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
            )}
          >
            <option value="">Select gender</option>
            {GENDERS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded-2xl border border-border bg-muted/60 p-4">
          <div>
            <h4 className="text-sm font-semibold text-text">Goals &amp; activity</h4>
            <p className="mt-1 text-xs leading-relaxed text-text-muted">
              How active you are and how quickly you want to progress.
            </p>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="flex min-w-0 flex-col gap-1.5">
              <label className="text-sm font-medium text-text">Activity level</label>
              <select
                value={activityLevel}
                onChange={(e) => setActivityLevel(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Select activity</option>
                {ACTIVITY_LEVELS.map((a) => (
                  <option key={a} value={a}>
                    {a.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label className="text-sm font-medium text-text">Goal</label>
              <select
                value={goalType}
                onChange={(e) => setGoalType(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Select goal</option>
                {GOAL_TYPES.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="exercise-frequency-select" className="text-sm font-medium text-text">
                Exercise frequency
              </label>
              <select
                id="exercise-frequency-select"
                data-testid="exercise-frequency-select"
                value={exerciseFrequency}
                onChange={(e) => setExerciseFrequency(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Not set</option>
                {EXERCISE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="exercise-type-select" className="text-sm font-medium text-text">
                Exercise type
              </label>
              <select
                id="exercise-type-select"
                data-testid="exercise-type-select"
                value={exerciseType}
                onChange={(e) => setExerciseType(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Not set</option>
                {EXERCISE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="avg-steps-select" className="text-sm font-medium text-text">
                Average daily steps
              </label>
              <select
                id="avg-steps-select"
                data-testid="avg-steps-select"
                value={stepsBucket}
                onChange={(e) => setStepsBucket(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Not set</option>
                {STEPS_OPTIONS.filter((option) => option.value !== "unknown").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="pace-select" className="text-sm font-medium text-text">
                Weight-loss pace
              </label>
              <select
                id="pace-select"
                data-testid="pace-select"
                value={weightLossPace}
                onChange={(e) => setWeightLossPace(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Not set</option>
                {PACE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                    {option.sublabel ? ` — ${option.sublabel}` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-muted/60 p-4">
          <div>
            <h4 className="text-sm font-semibold text-text">Preferences</h4>
            <p className="mt-1 text-xs leading-relaxed text-text-muted">
              How you like to eat, log, and stay motivated.
            </p>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="diet-type-select" className="text-sm font-medium text-text">
                Diet style
              </label>
              <select
                id="diet-type-select"
                data-testid="diet-type-select"
                value={dietType}
                onChange={(e) => {
                  if (isDietType(e.target.value)) setDietType(e.target.value);
                }}
                className={SELECT_CLASSES}
              >
                {DIET_TYPES.map((diet) => (
                  <option key={diet} value={diet}>
                    {diet.replace("-", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="motivation-select" className="text-sm font-medium text-text">
                Motivation
              </label>
              <select
                id="motivation-select"
                data-testid="motivation-select"
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Not set</option>
                {MOTIVATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="impact-select" className="text-sm font-medium text-text">
                Biggest impact
              </label>
              <select
                id="impact-select"
                data-testid="impact-select"
                value={impact}
                onChange={(e) => setImpact(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Not set</option>
                {IMPACT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="logging-style-select" className="text-sm font-medium text-text">
                Logging style
              </label>
              <select
                id="logging-style-select"
                data-testid="logging-style-select"
                value={loggingStyle}
                onChange={(e) => void handleLoggingStyleChange(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Not set</option>
                {LOGGING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <label htmlFor="eating-style-select" className="text-sm font-medium text-text">
                Eating style
              </label>
              <select
                id="eating-style-select"
                data-testid="eating-style-select"
                value={eatingStyle}
                onChange={(e) => setEatingStyle(e.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="">Not set</option>
                {EATING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">Health considerations</span>
          <div role="group" aria-label="Health considerations" className="flex flex-wrap gap-2">
            {SAFETY_OPTIONS.map((option) => {
              const active = safetyFlags.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => handleToggleSafetyFlag(option.value)}
                  className={cn(
                    "min-h-[44px] rounded-full border px-4 py-2 text-sm font-medium",
                    "select-none transition-all duration-200 ease-out active:scale-[0.98]",
                    active
                      ? "border-accent bg-accent-light text-text"
                      : "border-border bg-surface text-text-muted",
                  )}
                >
                  {option.title}
                </button>
              );
            })}
            <button
              type="button"
              aria-pressed={safetyFlags.length === 0}
              onClick={() => setSafetyFlags([])}
              className={cn(
                "min-h-[44px] rounded-full border px-4 py-2 text-sm font-medium",
                "select-none transition-all duration-200 ease-out active:scale-[0.98]",
                safetyFlags.length === 0
                  ? "border-accent bg-accent-light text-text"
                  : "border-border bg-surface text-text-muted",
              )}
            >
              None of these
            </button>
          </div>
          <p className="text-xs leading-relaxed text-text-muted">
            When any of these apply, automated calorie deficits are paused in favour of a
            maintenance plan and professional guidance.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-text">Weight unit</label>
            <UnitToggle
              value={unitWeight}
              options={["kg", "lbs"]}
              onChange={handleUnitWeightChange}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-text">Distance unit</label>
            <UnitToggle
              value={unitDistance}
              options={["km", "mi"]}
              onChange={setUnitDistance}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="timezone-select"
            className="flex items-center gap-2 text-sm font-medium text-text"
          >
            <Globe size={16} className="text-text-muted" />
            Timezone
          </label>
          <select
            id="timezone-select"
            data-testid="timezone-select"
            value={profileTimezone}
            onChange={(e) => void handleTimezoneChange(e.target.value)}
            className={SELECT_CLASSES}
          >
            <option value={AUTO_TIMEZONE}>Automatic (use device time zone)</option>
            {timezoneOptions.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
          <p className="text-xs leading-relaxed text-text-muted">
            Effective time zone: {effectiveTimezone}
            {profileTimezone === AUTO_TIMEZONE ? " (detected from your device)" : ""}.
            Used for daily totals, weigh-ins, and check-ins.
          </p>
          {timezoneStatus !== "idle" && (
            <p
              role={timezoneStatus === "error" ? "alert" : "status"}
              data-testid="timezone-save-status"
              className={cn(
                "text-xs",
                timezoneStatus === "error" ? "text-danger" : "text-text-muted",
              )}
            >
              {timezoneStatus === "saving"
                ? "Saving…"
                : timezoneStatus === "saved"
                  ? "Saved"
                  : "Could not save the time zone. Check your connection and try again."}
            </p>
          )}
        </div>

        <Button
          data-testid="profile-save-btn"
          variant="primary"
          size="md"
          loading={savingProfile}
          onClick={handleSaveProfile}
        >
          Save profile
        </Button>
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-2">
          <RotateCcw size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">Rerun onboarding</h3>
        </div>
        <p className="text-sm leading-relaxed text-text-muted">
          Walk through the setup questions again to refresh your programme. Your
          logged meals, weights, and memories are kept.
        </p>
        <Button
          data-testid="rerun-onboarding-btn"
          variant="secondary"
          size="md"
          onClick={() => {
            setRerunError(null);
            setRerunOpen(true);
          }}
        >
          Rerun onboarding
        </Button>
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">App tour</h3>
        </div>
        <p className="text-sm leading-relaxed text-text-muted">
          A quick walkthrough of Chat, Quick Log, Today, Plan, and Settings.
        </p>
        <Button
          data-testid="replay-tour-btn"
          variant="ghost"
          size="md"
          onClick={() => {
            console.log("[Frontend][Settings] App tour replay requested");
            requestAppTourReplay();
          }}
        >
          Replay app tour
        </Button>
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-2">
          <BookOpen size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">Guide</h3>
        </div>
        <p className="text-sm leading-relaxed text-text-muted">
          How BiteTrack works — the daily loop, the five screens, and everything
          you can ask the chat agent.
        </p>
        <Button
          data-testid="open-guide-btn"
          variant="ghost"
          size="md"
          disabled={onSwitchScreen === undefined}
          onClick={() => {
            console.log("[Frontend][Settings] Opening guide screen");
            onSwitchScreen?.("guide");
          }}
        >
          Open the guide
        </Button>
      </Card>
        </div>
      </details>

      {/* Section 2: Tracking */}
      <details data-testid="settings-section-tracking" className={SECTION_CLASSES}>
        <summary className={SECTION_SUMMARY_CLASSES}>
          <ListChecks size={20} className="text-text-muted" />
          <span>Tracking</span>
        </summary>
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-text-muted">
            Logging defaults applied when you send meals to Chat.
          </p>
          {preferences === undefined ? (
            <Skeleton className="h-12 rounded-xl" />
          ) : (
            <div className="flex flex-col gap-4">
              <Toggle
                data-testid="pref-auto-memory"
                label="Auto-save food memories"
                checked={preferences?.autoMemory ?? true}
                onCheckedChange={(v) => handleTogglePreference("autoMemory", v)}
              />
            </div>
          )}
          <div className="flex items-start gap-2 rounded-xl bg-muted px-4 py-3">
            <Info size={16} className="mt-0.5 shrink-0 text-text-muted" />
            <p className="text-sm leading-relaxed text-text-muted">
              Exercise calories never add to your food budget — logged workouts
              are tracked separately from what you eat.
            </p>
          </div>
        </div>
      </details>

      {/* Account: WhatsApp, data export, deletion, and support. AI credits and
          billing are managed by the parent Sapps app, not BiteTrack. */}
      <div className="flex flex-col gap-4">
      {/* WhatsApp */}
      <details className="rounded-2xl border border-border bg-surface p-5">
        <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-2 text-base font-semibold text-text active:opacity-80">
          <Smartphone size={20} className="text-text-muted" />
          <span>WhatsApp</span>
        </summary>
        <div className="mt-4 flex flex-col gap-4">

        <div data-testid="wa-api-status" className="flex flex-col gap-2">
          <p className="text-sm font-medium text-text">Graph API connection</p>
          {whatsAppLoading ? (
            <Skeleton className="h-8 rounded-lg" />
          ) : whatsAppError ? (
            <p className="text-sm text-danger" role="alert">
              Graph API connection status is unavailable.
            </p>
          ) : (
            <div data-testid="wa-status" className="flex items-center gap-2 text-sm">
              {whatsApp?.connected ? (
                <>
                  <Check size={16} className="text-accent" />
                  <span className="text-text">Connected</span>
                </>
              ) : (
                <span className="text-text-muted">Not connected</span>
              )}
            </div>
          )}
        </div>

        <div
          data-testid="wa-webhook-readiness"
          className="flex flex-col gap-2 rounded-xl border border-border bg-muted px-4 py-3"
        >
          <p className="text-sm font-medium text-text">Server webhook environment</p>
          {whatsAppWebhookLoading ? (
            <Skeleton className="h-8 rounded-lg" />
          ) : whatsAppWebhookError ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-danger" role="alert">
                Server webhook environment status is unavailable right now.
              </p>
              <Button
                data-testid="wa-webhook-retry-btn"
                variant="secondary"
                size="sm"
                onClick={loadWhatsAppWebhookReadiness}
              >
                Retry environment check
              </Button>
            </div>
          ) : whatsAppWebhook?.configured ? (
            <div className="flex items-center gap-2 text-sm">
              <Check size={16} className="text-accent" />
              <span className="text-text">Required webhook environment values are present</span>
            </div>
          ) : (
            <p className="text-sm text-text-muted">
              Required webhook environment values are missing. The app checks presence only; it
              does not validate Meta webhook setup.
            </p>
          )}
          <p className="text-xs leading-relaxed text-text-muted">
            Sapps consent collects only the WhatsApp API bearer token. The webhook verify token
            and app secret must be provisioned separately through the secure platform environment;
            their values are never shown here.
          </p>
        </div>

        <div
          data-testid="wa-webhook-url"
          className="rounded-lg bg-muted p-3 text-xs text-text-muted"
        >
          <p className="font-medium text-text">Meta callback URL</p>
          {whatsAppWebhookUrl ? (
            <p className="mt-1 break-all font-mono">{whatsAppWebhookUrl}</p>
          ) : (
            <p className="mt-1 text-danger" data-testid="wa-webhook-unavailable">
              Callback URL unavailable. Expose the app site URL before connecting WhatsApp.
            </p>
          )}
          <p className="mt-2 leading-relaxed">
            This URL identifies the webhook endpoint; it does not confirm Meta verification,
            subscription, or delivery.
          </p>
        </div>

        {whatsApp?.connected ? (
          <Button
            data-testid="wa-connect-btn"
            variant="secondary"
            size="md"
            loading={waBusy}
            onClick={handleDisconnectWhatsApp}
          >
            Disconnect
          </Button>
        ) : (
          <div className="flex flex-col gap-3">
            <Input
              data-testid="wa-phone-id-input"
              label="Phone number ID"
              value={waPhoneId}
              onChange={(e) => setWaPhoneId(e.target.value)}
              placeholder="1234567890"
            />
            <p className="text-xs leading-relaxed text-text-muted">
              Sapps consent collects only the WhatsApp API bearer token. The webhook verify token
              and app secret require separate secure platform provisioning and are never entered
              or displayed in the app.
            </p>
            <div className="rounded-xl bg-muted px-4 py-3 text-sm leading-relaxed text-text-muted">
              <p className="font-medium text-text">Meta test-number setup</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5">
                <li>
                  Create or open a Meta app in the{" "}
                  <a
                    className="text-accent underline active:opacity-80"
                    href="https://developers.facebook.com/apps/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Meta App Dashboard
                  </a>
                  .
                </li>
                <li>Add the WhatsApp product and open its API setup or test-number panel.</li>
                <li>Copy the Phone Number ID into the field above. Sapps securely collects only the WhatsApp API bearer token in the consent screen.</li>
                <li>Add a verified recipient phone number for the test number.</li>
                <li>In Meta Webhooks, use the callback URL shown above.</li>
                <li>
                  Separately provision the webhook verify token and app secret through the secure
                  platform environment; do not enter them in this app.
                </li>
                <li>Choose Verify and Save, then subscribe the app to the <code>messages</code> field.</li>
                <li>Send a test message from the verified recipient to the Meta test number.</li>
              </ol>
              <a
                className="mt-2 inline-flex min-h-[44px] items-center text-accent underline active:opacity-80"
                href="https://developers.facebook.com/docs/graph-api/webhooks/getting-started"
                target="_blank"
                rel="noreferrer"
              >
                Open Meta webhook setup guide
              </a>
            </div>
            <Button
              data-testid="wa-connect-btn"
              variant="primary"
              size="md"
              loading={waBusy}
              disabled={
                !waPhoneId.trim() ||
                whatsAppWebhookUrl === null
              }
              onClick={handleConnectWhatsApp}
            >
              Connect WhatsApp API token
            </Button>
          </div>
        )}

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text">
              {profile?.whatsappPhone ? (
                <span>
                  Linked: <span className="font-medium">{profile.whatsappPhone}</span>
                </span>
              ) : (
                <span className="text-text-muted">No WhatsApp number linked</span>
              )}
            </div>
            <Button
              data-testid="wa-link-new-btn"
              variant="secondary"
              size="sm"
              loading={linkingBusy}
              onClick={handleLinkNewNumber}
            >
              Link new number
            </Button>
          </div>
          {linkingCode && (
            <div className="mt-3 rounded-xl bg-accent-light p-3 text-center">
              <p className="text-xs text-text-muted">Your 6-digit linking code</p>
              <p
                data-testid="linking-code"
                className="text-2xl font-bold tracking-widest text-accent"
              >
                {linkingCode}
              </p>
              <p className="text-xs text-text-muted">
                Message this code to the WhatsApp bot. It expires in 10 minutes.
              </p>
            </div>
          )}
        </div>
        </div>
      </details>

      <Card className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-2">
          <Download size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">Export data</h3>
        </div>
        <p className="text-sm leading-relaxed text-text-muted">
          Download your profile, the last 90 days of food logs, your weight
          history, and your food memories as a JSON file.
        </p>
        <Button
          data-testid="export-data-btn"
          variant="secondary"
          size="md"
          loading={exportBusy}
          onClick={() => void handleExportData()}
        >
          Download JSON export
        </Button>
        {exportError && (
          <p
            data-testid="export-data-error"
            role="alert"
            className="text-sm text-danger"
          >
            {exportError}
          </p>
        )}
        {exportDone && (
          <p role="status" className="text-sm text-accent">
            Export downloaded.
          </p>
        )}
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-2">
          <TriangleAlert size={20} className="text-danger" />
          <h3 className="text-base font-semibold text-text">Delete account data</h3>
        </div>
        <p className="text-sm leading-relaxed text-text-muted">
          Delete all your data — meals, exercises, weights, memories, chat
          threads — and start onboarding again. This cannot be undone.
        </p>
        <Button
          data-testid="reset-everything-btn"
          variant="danger"
          size="md"
          onClick={() => {
            setResetError(null);
            setResetOpen(true);
          }}
        >
          Reset everything
        </Button>
      </Card>

      <Card className="flex flex-col gap-2 p-5">
        <p className="text-sm font-semibold text-text">Support and privacy</p>
        <a
          className="flex min-h-[44px] items-center gap-2 text-sm text-accent underline active:opacity-80"
          href="mailto:support@sapps.co"
        >
          <LifeBuoy size={16} />
          support@sapps.co
        </a>
        <a
          className="flex min-h-[44px] items-center gap-2 text-sm text-accent underline active:opacity-80"
          href="https://sapps.co/privacy"
          target="_blank"
          rel="noreferrer"
        >
          <ShieldCheck size={16} />
          Privacy policy
        </a>
      </Card>
      </div>

      {/* Section 3: Advanced */}
      <details data-testid="settings-section-advanced" className={SECTION_CLASSES}>
        <summary className={SECTION_SUMMARY_CLASSES}>
          <Wrench size={20} className="text-text-muted" />
          <span>Advanced</span>
        </summary>
        <div className="mt-4 flex flex-col gap-4">
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-2">
          <Search size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">AI Model</h3>
        </div>

        {modelsLoading ? (
          <Skeleton className="h-12 rounded-xl" />
        ) : modelsError ? (
          <p className="text-sm text-danger">{modelsError}</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-text">Model</label>
            <select
              data-testid="model-select"
              value={modelValue}
              onChange={(e) => handleModelChange(e.target.value)}
              className={cn(
                "w-full rounded-xl border border-border bg-surface py-3 px-4 text-base",
                "text-text focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
              )}
            >
              <option value="">Select a model</option>
              {chatModelOptions.map((modelId) => (
                <option key={modelId} value={modelId}>
                  {modelId}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedVisionModel === undefined ? (
          <Skeleton className="h-20 rounded-xl" />
        ) : (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="vision-model-select" className="text-sm font-medium text-text">
              Vision model
            </label>
            <select
              id="vision-model-select"
              data-testid="vision-model-select"
              value={visionModel}
              onChange={(event) => handleVisionModelChange(event.target.value)}
              className={cn(
                "min-h-[44px] w-full rounded-xl border border-border bg-surface py-3 px-4 text-base",
                "text-text focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
              )}
            >
              {visionModelOptions.map((modelId) => {
                const displayModel = models.find((model) => model.id === modelId);
                const label =
                  displayModel?.owned_by === undefined
                    ? modelId
                    : `${modelId} · ${displayModel.owned_by}`;
                return (
                  <option key={modelId} value={modelId}>
                    {label}
                  </option>
                );
              })}
            </select>
            <p className="text-xs text-text-muted">
              Used only when a chat prompt includes an image.
            </p>
          </div>
        )}

        <p className="text-xs text-text-muted">
          Powered by Sapps AI Gateway.
        </p>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-2">
          <AudioLines size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">Voice input</h3>
        </div>
        <p className="text-sm leading-relaxed text-text-muted">
          Choose how recorded meal notes are transcribed before they are added to Chat.
        </p>
        {audioPreferences === undefined ? (
          <Skeleton className="h-28 rounded-xl" />
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="audio-model-select" className="text-sm font-medium text-text">Audio model</label>
              <select id="audio-model-select" data-testid="audio-model-select" value={audioModel} onChange={(event) => handleAudioModelChange(event.target.value)} className={cn("min-h-[44px] w-full rounded-xl border border-border bg-surface py-3 px-4 text-base", "text-text focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent")}>
                {AUDIO_MODELS.map((model) => <option key={model.id} value={model.id}>{model.label}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="audio-language-select" className="text-sm font-medium text-text">Language</label>
              <select id="audio-language-select" data-testid="audio-language-select" value={audioLanguage} onChange={(event) => handleAudioLanguageChange(event.target.value)} className={cn("min-h-[44px] w-full rounded-xl border border-border bg-surface py-3 px-4 text-base", "text-text focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent")}>
                {AUDIO_LANGUAGES.map((language) => <option key={language.id} value={language.id}>{language.label}</option>)}
              </select>
            </div>
          </>
        )}
        <p className="text-xs text-text-muted">Default: Whisper v3 with automatic language detection.</p>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-2">
          <Sliders size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">Approvals and search</h3>
        </div>

        {preferences === undefined ? (
          <Skeleton className="h-12 rounded-xl" />
        ) : (
          <div className="flex flex-col gap-4">
            <Toggle
              data-testid="pref-always-ask"
              label="Always ask before logging"
              checked={preferences?.alwaysAsk ?? true}
              onCheckedChange={(v) => handleTogglePreference("alwaysAsk", v)}
            />
            <Toggle
              data-testid="pref-web-search"
              label="Enable web search"
              checked={preferences?.webSearch ?? true}
              onCheckedChange={(v) => handleTogglePreference("webSearch", v)}
            />
          </div>
        )}

        <div className="rounded-xl bg-muted px-4 py-3">
          <p className="text-sm leading-relaxed text-text">
            Web search is provided automatically by Sapps AI when available.
          </p>
          <p className="mt-1 text-xs leading-relaxed text-text-muted">
            No API key or connection is required.
          </p>
        </div>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">Chat agent instructions</h3>
        </div>
        <p className="text-sm leading-relaxed text-text-muted">
          Guides the assistant&apos;s tone and preferences. It can&apos;t override
          safety or calculation rules.
        </p>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="agent-instructions-textarea"
            className="text-sm font-medium text-text"
          >
            Custom instructions
          </label>
          <textarea
            id="agent-instructions-textarea"
            data-testid="agent-instructions-textarea"
            rows={4}
            maxLength={CHAT_AGENT_INSTRUCTIONS_MAX}
            value={agentInstructions}
            onChange={(e) => {
              setAgentInstructions(e.target.value);
              setAgentInstructionsStatus("idle");
            }}
            placeholder="Example: Always suggest high-protein desi alternatives. Keep answers short."
            className={cn(
              "w-full rounded-xl border border-border bg-surface py-3 px-4 text-base",
              "text-text focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
            )}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-text-muted">Leave blank to clear.</p>
            <p
              data-testid="agent-instructions-count"
              className="text-xs text-text-muted"
            >
              {agentInstructions.length}/{CHAT_AGENT_INSTRUCTIONS_MAX}
            </p>
          </div>
        </div>
        {agentInstructionsStatus === "error" && (
          <p role="alert" className="text-sm text-danger">
            Could not save instructions. Check your connection and try again.
          </p>
        )}
        {agentInstructionsStatus === "saved" && (
          <p
            role="status"
            data-testid="agent-instructions-saved"
            className="text-sm text-accent"
          >
            Instructions saved.
          </p>
        )}
        <Button
          data-testid="agent-instructions-save-btn"
          variant="secondary"
          size="md"
          loading={agentInstructionsStatus === "saving"}
          onClick={() => void handleSaveAgentInstructions()}
        >
          Save instructions
        </Button>
      </Card>

      <details className="rounded-2xl border border-border bg-surface p-5">
        <summary className="cursor-pointer list-none text-base font-semibold text-text active:opacity-80">Enable demo mode</summary>
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-text-muted">Set a password for the public demo. The install URL is where demo visitors are sent to get their own BiteTrack app.</p>
          <Toggle data-testid="demo-enabled-toggle" label="Enable public demo" checked={demoEnabled} onCheckedChange={(value) => { setDemoEnabled(value); setDemoError(null); setDemoSaved(false); }} />
          <Input data-testid="demo-install-url-input" label="Install redirect URL" type="url" inputMode="url" value={demoInstallUrl} onChange={(event) => { setDemoInstallUrl(event.target.value); setDemoError(null); setDemoSaved(false); }} placeholder="https://sapps.co/install/..." />
          <Input data-testid="demo-password-input" label={demoConfig?.enabled ? "New password (optional)" : "Password"} type="password" autoComplete="new-password" value={demoPassword} onChange={(event) => { setDemoPassword(event.target.value); setDemoError(null); setDemoSaved(false); }} placeholder={demoConfig?.enabled ? "Leave blank to keep current" : "Choose a demo password"} />
          {demoError && <p className="text-sm text-danger" role="alert">{demoError}</p>}
          {demoSaved && <p className="text-sm text-accent" role="status">Demo configuration saved.</p>}
          <Button data-testid="demo-config-save-btn" loading={demoSaving} onClick={() => void handleSaveDemoConfig()}>Save demo settings</Button>
        </div>
      </details>

      <div className="flex flex-col gap-1 rounded-2xl border border-border bg-muted/60 p-4">
        <button
          type="button"
          data-testid="calculation-audit-link"
          disabled={onSwitchScreen === undefined}
          onClick={() => {
            console.log("[Frontend][Settings] Opening calculation audit on Plan screen");
            onSwitchScreen?.("plan");
          }}
          className="flex min-h-[44px] items-center gap-2 select-none text-left text-sm font-medium text-accent underline transition-opacity active:opacity-80 disabled:opacity-50"
        >
          Calculation audit — see how your targets are computed
        </button>
        <div className="flex min-h-[44px] items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-2 text-text-muted">
            <Gauge size={16} />
            Calculation engine
          </span>
          <span data-testid="engine-version-line" className="font-medium text-text">
            {engineState === undefined
              ? "Loading…"
              : (engineState?.engineVersion ?? "Not initialized")}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1 rounded-2xl border border-border bg-muted/60 p-4">
        <div className="flex items-center gap-2">
          <Info size={16} className="text-text-muted" />
          <h4 className="text-sm font-semibold text-text">About</h4>
        </div>
        <p className="text-sm text-text-muted">Version 1.50.0</p>
        <p className="text-sm text-text-muted">Powered by Sapps AI Gateway</p>
      </div>
        </div>
      </details>

      {resetOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Reset everything?"
        >
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              if (!resetBusy) setResetOpen(false);
            }}
            className="absolute inset-0 bg-black/40"
          />
          <div className="ob-sheet-up relative w-full max-w-xl rounded-t-3xl border-t border-border bg-surface px-6 pb-[max(env(safe-area-inset-bottom),2rem)] pt-6">
            <h2 className="text-xl font-bold text-text">Reset everything?</h2>
            <p className="mt-2 text-sm leading-relaxed text-text-muted">
              This permanently deletes ALL your BiteTrack data and restarts
              onboarding. There is no undo.
            </p>
            {resetError && (
              <p role="alert" className="mt-3 text-sm text-danger">
                {resetError}
              </p>
            )}
            <div className="mt-6 flex flex-col gap-3">
              <Button
                data-testid="reset-everything-confirm"
                variant="danger"
                size="lg"
                className="w-full"
                loading={resetBusy}
                onClick={() => void handleResetEverything()}
              >
                Yes, delete everything
              </Button>
              <button
                type="button"
                onClick={() => setResetOpen(false)}
                disabled={resetBusy}
                className="min-h-[44px] select-none text-sm font-medium text-text-muted transition-opacity active:opacity-80 disabled:opacity-50"
              >
                Keep my data
              </button>
            </div>
          </div>
        </div>
      )}

      {rerunOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Rerun onboarding?"
        >
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              if (!rerunBusy) setRerunOpen(false);
            }}
            className="absolute inset-0 bg-black/40"
          />
          <div className="ob-sheet-up relative w-full max-w-xl rounded-t-3xl border-t border-border bg-surface px-6 pb-[max(env(safe-area-inset-bottom),2rem)] pt-6">
            <h2 className="text-xl font-bold text-text">Rerun onboarding?</h2>
            <p className="mt-2 text-sm leading-relaxed text-text-muted">
              You will re-enter your programme answers from scratch — your
              current answers are replaced as you go. Your logged meals,
              weights, memories, and chat history stay safe.
            </p>
            {rerunError && (
              <p role="alert" className="mt-3 text-sm text-danger">
                {rerunError}
              </p>
            )}
            <div className="mt-6 flex flex-col gap-3">
              <Button
                data-testid="rerun-onboarding-confirm"
                variant="primary"
                size="lg"
                className="w-full"
                loading={rerunBusy}
                onClick={() => void handleRerunOnboarding()}
              >
                Yes, rerun onboarding
              </Button>
              <button
                type="button"
                onClick={() => setRerunOpen(false)}
                disabled={rerunBusy}
                className="min-h-[44px] select-none text-sm font-medium text-text-muted transition-opacity active:opacity-80 disabled:opacity-50"
              >
                Stay in Settings
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
